import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Title } from 'react-admin';
import {
    Box, Card, CardContent, Typography, Chip, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField,
    InputAdornment, IconButton, Tooltip, CircularProgress, Alert,
    Tabs, Tab, Badge, Avatar, Stack, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ForumIcon from '@mui/icons-material/Forum';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { API_URL, AGENT_SECRET } from './config';

import { usePushNotifications } from '../../hooks/usePushNotifications';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';


const timeAgo = (d) => {
    const diff = Date.now() - new Date(d).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const KpiCard = ({ label, value, icon, color, bg }) => (
    <Card sx={{ flex: 1, minWidth: 150, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.25}>{label}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: bg, width: 48, height: 48 }}>{icon}</Avatar>
            </Stack>
        </CardContent>
    </Card>
);

export default function ChatAdmin() {
    const navigate = useNavigate();
    const [chats, setChats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(0);
    const [search, setSearch] = useState('');
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const { supported, subscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();


    const fetchChats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch(`${API_URL}/adminGetChats`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentSecret: AGENT_SECRET }),
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'Failed to load chats');
            setChats(data.chats || []);
            setLastRefresh(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchChats(); }, [fetchChats]);

    const kpis = {
        total:      chats.length,
        open:       chats.filter(c => c.status === 'open').length,
        closed:     chats.filter(c => c.status === 'closed').length,
        needsReply: chats.filter(c => c.status === 'open' && c.unread_count > 0).length,
    };

    const tabFilter = [
        chats,
        chats.filter(c => c.status === 'open'),
        chats.filter(c => c.status === 'open' && c.unread_count > 0),
        chats.filter(c => c.status === 'closed'),
    ][tab];

    const filtered = tabFilter.filter(c =>
        !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.user_email || '').toLowerCase().includes(search.toLowerCase()) ||
        c.user_id.toLowerCase().includes(search.toLowerCase())
    );

    const sorted = [...filtered].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

    return (
        <Box sx={{ p: 3, width: '100%' }}>
            <Title title="Support Chats" />

            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                
                {supported && (
                <Tooltip title={subscribed ? 'Disable notifications' : 'Enable notifications'}>
                    <IconButton onClick={subscribed ? unsubscribe : subscribe} disabled={pushLoading}>
                    {subscribed
                        ? <NotificationsIcon sx={{ color: '#6366f1' }} />
                        : <NotificationsOffIcon />}
                    </IconButton>
                </Tooltip>
                )}
                <Box>
                    <Typography variant="h5" fontWeight={700}>Support Chats</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Last refresh: {lastRefresh.toLocaleTimeString()}
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchChats} disabled={loading}>
                        <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
                <KpiCard label="Total Chats" value={kpis.total} icon={<ForumIcon sx={{ color: '#6366f1' }} />} color="#6366f1" bg="#ede9fe" />
                <KpiCard label="Open" value={kpis.open} icon={<ChatBubbleIcon sx={{ color: '#0ea5e9' }} />} color="#0ea5e9" bg="#e0f2fe" />
                <KpiCard label="Needs Reply" value={kpis.needsReply} icon={<PendingActionsIcon sx={{ color: '#f59e0b' }} />} color="#f59e0b" bg="#fef3c7" />
                <KpiCard label="Closed" value={kpis.closed} icon={<CheckCircleOutlineIcon sx={{ color: '#10b981' }} />} color="#10b981" bg="#d1fae5" />
            </Stack>

            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} pb={1}>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40 }}>
                            <Tab label="All" sx={{ minHeight: 40, py: 0 }} />
                            <Tab label={<Badge badgeContent={kpis.open} color="primary" sx={{ pr: 1 }}>Open</Badge>} sx={{ minHeight: 40, py: 0 }} />
                            <Tab label={<Badge badgeContent={kpis.needsReply} color="warning" sx={{ pr: 1 }}>Needs Reply</Badge>} sx={{ minHeight: 40, py: 0 }} />
                            <Tab label="Closed" sx={{ minHeight: 40, py: 0 }} />
                        </Tabs>
                        <TextField
                            size="small"
                            placeholder="Search by name or user…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }
                            }}
                            sx={{ minWidth: 260 }}
                        />
                    </Stack>
                </Box>

                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
                ) : sorted.length === 0 ? (
                    <Box textAlign="center" p={6} color="text.secondary">
                        <ForumIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography>No chats found</Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Conversation</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Last Message</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Unread</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sorted.map(chat => (
                                    <TableRow
                                        key={chat.chat_id}
                                        hover
                                        onClick={() => navigate(`/chats/${chat.chat_id}`)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: chat.unread_count > 0 && chat.status === 'open' ? 'warning.50' : 'inherit',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#6366f1', fontSize: 14 }}>
                                                    {chat.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={chat.unread_count > 0 ? 700 : 400}>
                                                    {chat.name}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                        <Typography variant="body2" fontWeight={500} fontSize={13}>
                                            {chat.user_username || '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" fontSize={11}>
                                            {chat.user_email || chat.user_id}
                                        </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 220 }}>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {chat.last_message || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {timeAgo(chat.updated_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={chat.status}
                                                size="small"
                                                color={chat.status === 'open' ? 'success' : 'default'}
                                                variant="outlined"
                                                sx={{ textTransform: 'capitalize', fontSize: 11 }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {chat.unread_count > 0 ? (
                                                <Chip label={chat.unread_count} size="small" color="warning" sx={{ minWidth: 28, fontSize: 11, fontWeight: 700 }} />
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <ArrowForwardIosIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Divider />
                <Box px={2} py={1}>
                    <Typography variant="caption" color="text.secondary">
                        {sorted.length} conversation{sorted.length !== 1 ? 's' : ''} shown
                    </Typography>
                </Box>
            </Card>

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </Box>
    );
}